/* ──────────────────────────────────────────────────────────────────
   Design Vote poll page.

   Three steps: ballot -> (optional) contact capture -> thank you.

   The capture step is skipped entirely when we already have an email OR a
   phone for this person — either channel is enough to tell them which design
   won. See resolveKnownContact().

   See sections/design-vote.liquid for the flow contract.

   Reuses window.LeadCapture (assets/lead-capture.js) for email validation,
   typo suggestions, the phone country picker and the identity lookup, and
   window.instameIdentify (snippets/user-identity.liquid) for Klaviyo.
─────────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  const root = document.querySelector("[data-dv-root]");
  if (!root) return;

  const MAX_PICKS = 3;
  const SOURCE = "design_vote";
  const UPSELL_COLLECTION = "best-sellers";

  // Same host→backend resolution as photo-to-art.js. Keep in sync.
  const API_BASE =
    /(^|\.)(instame\.shop|lilaland\.co|lilaland\.pet)$/i.test(location.hostname) ||
    /\.myshopify\.com$/i.test(location.hostname)
      ? "https://api.lilaland.co"
      : location.hostname === "localhost" || location.hostname === "127.0.0.1"
        ? "http://localhost:3000" // direct `shopify theme dev` → local backend
        : ""; // other non-prod hosts (Tailscale/phone) → same-origin dev gateway

  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => Array.from(root.querySelectorAll(sel));

  const state = {
    userId: null,
    selected: [], // design ids, in tap order
    voted: false,
    phone: null, // handle returned by LeadCapture.bindPhoneCountry
    knownEmail: "",
    knownPhone: "",
    knownVia: "", // link_email | link_phone | stored | "" — for analytics only
  };

  // ── Boot ────────────────────────────────────────────────────────────

  state.userId =
    window.instameUserId || localStorage.getItem("instame_user_id") || null;

  const designs = readDesigns();
  if (!designs.length) {
    console.warn("[design-vote] no designs configured");
    return;
  }

  renderGrid(designs);
  bindSubmit();
  bindCapture();

  // Prefetch whether we already know this person, so the post-vote branch is
  // instant rather than sitting on a spinner. Fire-and-forget: if it hasn't
  // resolved by the time they vote, submitVote awaits it.
  //
  // The catch matters more than it looks: submitVote awaits this AFTER the
  // ballot has already been recorded, so an unhandled rejection here would
  // strand the voter on a dead "Recording your vote…" button with their vote
  // silently counted. Falling back to `false` shows the capture card instead —
  // the worst case is asking for an email we may already have.
  const identityPromise = resolveKnownContact().catch((e) => {
    console.warn("[design-vote] identity lookup failed", e);
    return false;
  });

  // ── Ballot ──────────────────────────────────────────────────────────

  function readDesigns() {
    const el = $("[data-dv-designs]");
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed)
        ? parsed.filter((d) => d && typeof d.id === "string")
        : [];
    } catch (e) {
      console.warn("[design-vote] bad designs JSON", e);
      return [];
    }
  }

  function renderGrid(list) {
    const grid = $("[data-dv-grid]");
    if (!grid) return;

    grid.innerHTML = "";

    list.forEach((design) => {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "dv__card";
      card.setAttribute("data-dv-card", design.id);
      // aria-pressed doubles as the selected-state hook in CSS, so the
      // accessible state and the visual state can never drift apart.
      card.setAttribute("aria-pressed", "false");

      const thumb = document.createElement("span");
      thumb.className = "dv__card-thumb";
      if (design.image) {
        const img = document.createElement("img");
        img.src = design.image;
        img.alt = design.label || design.id;
        img.loading = "lazy";
        img.decoding = "async";
        thumb.appendChild(img);
      }

      const check = document.createElement("span");
      check.className = "dv__card-check";
      check.setAttribute("aria-hidden", "true");
      check.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

      const label = document.createElement("span");
      label.className = "dv__card-label";
      label.textContent = design.label || design.id;

      card.append(thumb, check, label);
      card.addEventListener("click", () => toggle(design.id, card));
      grid.appendChild(card);
    });

    refreshBallotState();
  }

  function toggle(id, card) {
    if (state.voted) return;

    const idx = state.selected.indexOf(id);

    if (idx > -1) {
      state.selected.splice(idx, 1);
    } else {
      // The 4th tap is a deliberate silent no-op — the subtitle already states
      // the limit, and firing an error for an action the user was warned about
      // reads as the page being broken. The dimmed unpicked cards plus the
      // count line carry the message instead.
      if (state.selected.length >= MAX_PICKS) {
        announce(
          `You've picked ${MAX_PICKS}. Tap one again to swap it out.`,
        );
        return;
      }
      state.selected.push(id);
    }

    card.setAttribute("aria-pressed", idx > -1 ? "false" : "true");
    refreshBallotState();
  }

  function refreshBallotState() {
    const grid = $("[data-dv-grid]");
    const submit = $("[data-dv-submit]");
    const n = state.selected.length;

    if (grid) grid.setAttribute("data-dv-full", n >= MAX_PICKS ? "1" : "0");
    if (submit) submit.disabled = n === 0;

    if (n === 0) {
      announce("");
    } else if (n >= MAX_PICKS) {
      announce(`${n} of ${MAX_PICKS} picked. Tap one again to swap it out.`);
    } else {
      announce(`${n} of ${MAX_PICKS} picked.`);
    }
  }

  function announce(text) {
    const el = $("[data-dv-count]");
    if (el) el.textContent = text;
  }

  // ── Vote submission ─────────────────────────────────────────────────

  function bindSubmit() {
    const btn = $("[data-dv-submit]");
    if (!btn) return;
    btn.addEventListener("click", submitVote);
  }

  async function submitVote() {
    if (state.voted || state.selected.length === 0) return;

    const btn = $("[data-dv-submit]");
    const picks = state.selected.slice();
    // Write to the inner .button-text span, not the button itself — the theme's
    // button markup depends on that span existing.
    const btnLabel = btn.querySelector(".button-text") || btn;

    btn.disabled = true;
    btnLabel.textContent = "Recording your vote…";

    try {
      const res = await fetch(`${API_BASE}/api/design-vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: state.userId, design_ids: picks }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      // Never pretend a vote landed when it didn't — that's the one thing this
      // page exists to do. Re-enable and let them retry.
      console.warn("[design-vote] vote failed", e);
      btn.disabled = false;
      btnLabel.textContent = "Vote";
      announce("Something went wrong. Please try again.");
      track("design_vote_failed", { message: (e && e.message) || String(e) });
      return;
    }

    state.voted = true;
    track("design_vote_submitted", {
      designs: picks,
      pick_count: picks.length,
    });

    // Known-contact check may still be in flight; it's a fast read and the vote
    // POST usually covers it, so this rarely blocks.
    const known = await identityPromise;

    track("design_vote_capture_routed", {
      skipped_capture: known,
      known_via: state.knownVia || null,
      has_email: !!state.knownEmail,
      has_phone: !!state.knownPhone,
    });

    if (known) {
      goTo("thanks");
    } else {
      goTo("capture");
    }
  }

  // ── Identity ────────────────────────────────────────────────────────

  /**
   * Do we already have ANY way to reach this person?
   *
   * Either channel is enough — email OR phone. This page is a poll, not the
   * lead-gen funnel: the only thing the contact buys us is being able to tell
   * them which design won, and one reachable channel does that. (Instant
   * preview deliberately asks for both; do not copy this rule over to it.)
   *
   * Three sources, cheapest first:
   *   1. `?e=` on the URL — Klaviyo stamps the recipient's address into the
   *      blast link, so anyone arriving from the email covers it without a
   *      round trip.
   *   2. `?p=` on the URL — same trick for an SMS blast, where the recipient
   *      may have no email on file at all.
   *   3. users.email / users.phone for this browser's anon id.
   *
   * The link paths identify (never subscribe): they're already a subscriber
   * (they got the blast), and subscribing without consent copy on screen would
   * violate the rule in CLAUDE.md. Only the capture card subscribes.
   */
  async function resolveKnownContact() {
    const emailFromLink = getQueryParam("e") || getQueryParam("email");
    const phoneFromLink = normalizePhone(
      getQueryParam("p") || getQueryParam("phone"),
    );

    const linkEmail = isValidEmail(emailFromLink) ? emailFromLink.trim() : "";
    const linkPhone = isValidE164(phoneFromLink) ? phoneFromLink : "";

    if (linkEmail || linkPhone) {
      state.knownEmail = linkEmail;
      state.knownPhone = linkPhone;
      state.knownVia = linkEmail ? "link_email" : "link_phone";
      if (typeof window.instameIdentify === "function") {
        // No subscribe flags: identify only. See above.
        window.instameIdentify(linkEmail || null, linkPhone || null, {
          source: SOURCE,
          customSource: "Design Vote",
        });
      }
      return true;
    }

    if (
      !state.userId ||
      !window.LeadCapture ||
      typeof window.LeadCapture.fetchIdentity !== "function"
    ) {
      return false;
    }

    const identity = await window.LeadCapture.fetchIdentity(
      state.userId,
      API_BASE,
    );
    if (identity && (identity.email || identity.phone)) {
      state.knownEmail = identity.email || "";
      state.knownPhone = identity.phone || "";
      state.knownVia = "stored";
      return true;
    }
    return false;
  }

  function getQueryParam(name) {
    try {
      return new URLSearchParams(location.search).get(name) || "";
    } catch (e) {
      return "";
    }
  }

  function isValidEmail(value) {
    if (window.LeadCapture && typeof window.LeadCapture.isValidEmail === "function") {
      return window.LeadCapture.isValidEmail(value);
    }
    return /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(String(value || "").trim());
  }

  /**
   * Coerce a phone from the link into E.164.
   *
   * Deliberately forgiving about the leading `+`: an unencoded `+` in a query
   * string decodes to a space, so a link built without `|urlencode` arrives as
   * " 15551234567". Rebuilding from the digits recovers that case instead of
   * showing a form to someone we can already reach. Bare 10 digits are assumed
   * US — the store only ships to the US.
   *
   * But it refuses anything carrying letters or an `@` first. A campaign wired
   * up as `?p={{ person.email }}` would otherwise have its digits harvested —
   * "jane19851201@x.com" reduces to a plausible-looking "+19851201" and we'd
   * push that junk number to Klaviyo and Supabase. Better to show the form.
   */
  function normalizePhone(raw) {
    const str = String(raw || "");
    if (/[a-z@]/i.test(str)) return "";
    const digits = str.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 10) return `+1${digits}`;
    return `+${digits}`;
  }

  // E.164: country code + national number, 15 digits max. Loose lower bound —
  // this only decides whether to show a form, it never feeds Klaviyo a number
  // the user hasn't confirmed.
  function isValidE164(value) {
    return /^\+[1-9]\d{7,14}$/.test(String(value || ""));
  }

  // ── Capture step ────────────────────────────────────────────────────

  function bindCapture() {
    const form = $("[data-dv-capture-form]");
    const emailInput = $("[data-dv-email-input]");
    const submitBtn = $("[data-dv-capture-submit]");
    const skipBtn = $("[data-dv-skip]");
    if (!form || !emailInput || !submitBtn) return;

    if (skipBtn) {
      skipBtn.addEventListener("click", () => {
        track("design_vote_capture_skipped", {});
        goTo("thanks");
      });
    }

    // Phone country pill. Returns a handle exposing the selected dial code;
    // null if the markup is missing, in which case phone is simply unavailable
    // and the email path still works.
    if (
      window.LeadCapture &&
      typeof window.LeadCapture.bindPhoneCountry === "function"
    ) {
      state.phone = window.LeadCapture.bindPhoneCountry({
        wrap: $("[data-lc-phone-wrap]"),
        trigger: $("[data-lc-phone-country]"),
        menu: $("[data-lc-phone-menu]"),
        flagEl: $("[data-lc-phone-flag]"),
        dialEl: $("[data-lc-phone-dial]"),
        input: $("[data-lc-phone-input]"),
        defaultCountry: "US",
      });
    }

    // "Did you mean …?" — suggest-only, never blocks submit, never
    // auto-replaces, so a real-but-rare domain is safe.
    const suggestEl = $("[data-dv-email-suggest]");
    const suggestBtn = $("[data-dv-email-suggest-btn]");
    let suggestion = null;
    const canSuggest =
      !!suggestEl &&
      !!suggestBtn &&
      !!window.LeadCapture &&
      typeof window.LeadCapture.suggestEmail === "function";

    const looksComplete = (v) => /@[^@\s]+\.[^@\s]{2,}$/.test(v);

    const refreshSuggestion = () => {
      if (!canSuggest) return;
      try {
        const val = (emailInput.value || "").trim();
        const s = looksComplete(val) ? window.LeadCapture.suggestEmail(val) : null;
        if (s && s.toLowerCase() !== val.toLowerCase()) {
          suggestion = s;
          suggestBtn.textContent = s;
          suggestEl.hidden = false;
        } else {
          suggestion = null;
          suggestEl.hidden = true;
        }
      } catch (e) {
        suggestion = null;
        suggestEl.hidden = true;
      }
    };

    if (canSuggest) {
      suggestBtn.addEventListener("click", () => {
        if (!suggestion) return;
        emailInput.value = suggestion;
        suggestion = null;
        suggestEl.hidden = true;
        refreshSubmitState();
        emailInput.focus();
      });
    }

    const refreshSubmitState = () => {
      submitBtn.disabled = !isValidEmail((emailInput.value || "").trim());
    };

    emailInput.addEventListener("input", () => {
      refreshSubmitState();
      refreshSuggestion();
    });
    emailInput.addEventListener("blur", refreshSuggestion);
    refreshSubmitState();

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submitCapture();
    });

    function submitCapture() {
      const email = (emailInput.value || "").trim();
      const phoneInput = $("[data-lc-phone-input]");
      const country = (state.phone && state.phone.country) || "US";
      // Prefer the handle's own accessors (they own the dial code and strip a
      // typed/pasted leading country code); fall back to reading the input
      // directly if bindPhoneCountry never mounted.
      const localDigits = state.phone
        ? state.phone.getDigits()
        : normalizeNsn(
            ((phoneInput && phoneInput.value) || "").replace(/\D/g, ""),
            country,
          );
      let phone = state.phone
        ? state.phone.getE164()
        : localDigits
          ? `+1${localDigits}`
          : "";

      if (!isValidEmail(email)) {
        showError("Please enter a valid email, or skip this step.");
        emailInput.focus();
        return;
      }

      // Phone is optional, so a number that still fails validation after
      // normalization must not block the capture. A malformed one must not be
      // sent either — Klaviyo drops profiles whose phone_number isn't valid
      // E.164 — so the bad number is dropped and we continue with email only.
      let phoneDropped = false;
      if (
        localDigits &&
        window.LeadCapture &&
        typeof window.LeadCapture.isValidPhone === "function" &&
        !window.LeadCapture.isValidPhone(localDigits, country)
      ) {
        phone = "";
        phoneDropped = true;
      }

      showError(null);

      // subscribeEmail/subscribeSms turn the profile into a real marketing
      // subscriber — identify alone only ever creates NEVER_SUBSCRIBED.
      // Consent is the disclosure copy under this button; the two ship together.
      if (typeof window.instameIdentify === "function") {
        window.instameIdentify(email, phone || null, {
          source: SOURCE,
          customSource: "Design Vote",
          subscribeEmail: true,
          subscribeSms: !!phone,
        });
      }

      const captureProps = { has_phone: !!phone };
      if (phoneDropped) {
        captureProps.reason = "invalid_phone_dropped";
      }
      track("design_vote_capture_submitted", captureProps);

      if (phoneDropped) {
        // Hold the panel just long enough for the notice to be readable —
        // an instant switch to the thanks step would hide it unread. The
        // email capture itself already went through above.
        showNotice(
          "We couldn't verify that phone number, so we saved your email only.",
        );
        if (submitBtn) {
          submitBtn.disabled = true;
        }
        setTimeout(() => {
          goTo("thanks");
        }, /* delay */ 2000);
        return;
      }

      goTo("thanks");
    }
  }

  // Guarded like isValidPhone above: an older cached lead-capture.js may
  // predate normalizeNsn, in which case the raw digits pass through.
  function normalizeNsn(digits, country) {
    return window.LeadCapture &&
      typeof window.LeadCapture.normalizeNsn === "function"
      ? window.LeadCapture.normalizeNsn(digits, country)
      : digits;
  }

  function showError(message) {
    const el = $("[data-dv-error]");
    if (!el) return;
    // A prior non-blocking notice may have neutralized the color — real
    // errors (and clears) restore the stylesheet's error styling.
    el.style.removeProperty("color");
    if (!message) {
      el.hidden = true;
      el.textContent = "";
      return;
    }
    el.textContent = message;
    el.hidden = false;
  }

  // Same slot as showError, but styled as plain text — for FYI messages
  // that must not read as a blocking error.
  function showNotice(message) {
    showError(message);
    const el = $("[data-dv-error]");
    if (el && message) {
      el.style.color = "inherit";
    }
  }

  // ── Steps ───────────────────────────────────────────────────────────

  function goTo(step) {
    $$("[data-dv-step]").forEach((el) => {
      el.classList.toggle("is-active", el.getAttribute("data-dv-step") === step);
    });

    root.scrollIntoView({ behavior: "smooth", block: "start" });

    if (step === "thanks") loadUpsell();
  }

  // ── Best-sellers shelf ──────────────────────────────────────────────

  /**
   * Fetches the best-sellers grid from the p2a-upsell alternate template.
   *
   * Full page fetch, not the Section Rendering API — the latter strips the
   * nested static blocks (gallery, title, price) and leaves bare CTA buttons.
   * Same approach and same reasoning as loadUpsell in photo-to-art.js; see
   * docs/shopify-theme-gotchas.md.
   */
  async function loadUpsell() {
    const container = $("[data-dv-upsell]");
    const grid = $("[data-dv-upsell-grid]");
    if (!container || !grid || container.dataset.dvLoaded === "1") return;
    container.dataset.dvLoaded = "1";

    try {
      const res = await fetch(
        `/collections/${encodeURIComponent(UPSELL_COLLECTION)}?view=p2a-upsell`,
        { headers: { Accept: "text/html" } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const section = doc.querySelector(".p2a-upsell-grid-section");
      if (!section) throw new Error("upsell section missing in response");

      grid.innerHTML = section.innerHTML;
      container.hidden = false;
    } catch (e) {
      // A missing shelf is cosmetic. The thank-you message is the point.
      console.warn("[design-vote] upsell load failed", e);
      container.hidden = true;
    }
  }

  // ── Tracking ────────────────────────────────────────────────────────

  // PostHog's replay session id (a UUIDv7 minted by posthog-js). Guarded:
  // before posthog-js finishes loading, the stub's get_session_id returns
  // undefined and the event simply goes unlinked.
  function phSessionId() {
    try {
      if (window.posthog && typeof window.posthog.get_session_id === "function") {
        const s = window.posthog.get_session_id();
        if (typeof s === "string" && s) {
          return s;
        }
      }
    } catch (_) {}
    return null;
  }

  // $session_id links the server-captured event to the session replay.
  function track(event, properties) {
    try {
      if (!state.userId) return;
      const replaySessionId = phSessionId();
      fetch(`${API_BASE}/api/events/posthog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: state.userId,
          event,
          properties: Object.assign(
            { tool: "design_vote" },
            window.instameExperiments || {},
            replaySessionId ? { "$session_id": replaySessionId } : {},
            properties || {},
          ),
        }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) {}
  }
})();
